import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import { Users } from "lucide-react";
import Overview from "./Pages/Overview";
import Campaigns from "./Pages/Campaigns";
import ReferredUsers from "./Pages/ReferredUsers";
import Commission from "./Pages/Commission";
import Faq from "./Pages/Faq";

import storage from "utils/Storage";

const PageGrid = styled.div`
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 24px;
  margin-top: 30px;
  align-items: start;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const SidebarNav = styled.aside`
  background-color: #122129;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: sticky;
  top: 80px;
  align-self: flex-start;

  @media (max-width: 992px) {
    flex-direction: row;
    overflow-x: auto;
    white-space: nowrap;
    position: static;
    padding: 8px;
    gap: 8px;

    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

const NavItem = styled.button`
  background-color: ${props => props.active ? "#213743" : "transparent"};
  color: ${props => props.active ? "#fff" : "#b1bad3"};
  border: none;
  border-radius: 6px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;

  &:hover {
    color: #fff;
    background-color: ${props => props.active ? "#213743" : "rgba(255, 255, 255, 0.04)"};
  }

  @media (max-width: 992px) {
    text-align: center;
    padding: 8px 12px;
    font-size: 13px;
  }
`;

const ContentPanel = styled.section`
  min-width: 0;
`;

const PageWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 30px 20px;

  @media (max-width: 768px) {
    padding: 16px 10px !important;
  }
`;

const AfterLogin = () => {
  const history = useHistory();
  const location = useLocation();  
  const isLogged = !!storage.getKey('logged');

  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 992);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 992);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const menuItems = isLogged ? [
    { text: 'Overview', route: '/affiliate' },
    { text: 'Campaigns', route: '/affiliate/campaigns' },
    { text: 'Commission', route: '/affiliate/commission' },
    { text: 'Referred Users', route: '/affiliate/referred-users' },
    { text: 'FAQ', route: '/affiliate/faq' },
  ] : [
    { text: 'Overview', route: '/affiliate' },
  ];

  const activeRoute = location.pathname;

  return (
    <PageWrapper>
      <style>{`
        .affiliate-scroll-tabs::-webkit-scrollbar {
          display: none !important;
        }
      `}</style>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "24px" }}>
        <Users size={28} color="#ffffff" style={{ strokeWidth: 2.5 }} />
        <h1 style={{ fontSize: "28px", fontWeight: "800", color: "#ffffff", margin: 0, letterSpacing: "-0.5px" }}>Affiliate Program</h1>
      </div>
      <PageGrid>
        <div 
          className="affiliate-scroll-tabs"
          style={isMobile ? {
            display: "flex",
            gap: "8px",
            overflowX: "auto",
            whiteSpace: "nowrap",
            padding: "10px 12px",
            background: "#122129",
            borderRadius: "8px",
            border: "1px solid rgba(255, 255, 255, 0.05)",
            width: "100%",
            boxSizing: "border-box",
            msOverflowStyle: "none",
            scrollbarWidth: "none"
          } : {
            display: "flex",
            flexDirection: "column",
            gap: "6px",
            padding: "12px 8px",
            background: "#122129",
            borderRadius: "8px",
            border: "1px solid rgba(255, 255, 255, 0.05)",
            width: "100%",
            alignSelf: "flex-start",
            boxSizing: "border-box"
          }}
        >
          {menuItems.map((item, index) => {
            const isActive = activeRoute === item.route;
            return (
              <button
                key={index}
                onClick={() => history.push(item.route)}
                style={{
                  background: isActive ? '#2f4553' : 'transparent',
                  color: isActive ? '#fff' : '#b1bad3',
                  border: isActive ? '1px solid #4e606d' : '1px solid rgba(255, 255, 255, 0.08)',
                  borderRadius: '6px',
                  padding: isMobile ? '8px 16px' : '12px 16px',
                  fontSize: isMobile ? '13px' : '14px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  textAlign: isMobile ? 'center' : 'left',
                  transition: 'all 0.15s ease',
                  width: isMobile ? 'auto' : '100%',
                  display: isMobile ? 'inline-block' : 'block',
                  outline: 'none',
                  whiteSpace: 'nowrap'
                }}
              >
                {item.text}
              </button>
            );
          })}
        </div>

        <ContentPanel>
          {(!isLogged || activeRoute === '/affiliate') && <Overview />}
          {isLogged && activeRoute === '/affiliate/campaigns' && <Campaigns />}
          {isLogged && activeRoute === '/affiliate/commission' && <Commission />}
          {isLogged && activeRoute === '/affiliate/referred-users' && <ReferredUsers />}
          {isLogged && activeRoute === '/affiliate/faq' && <Faq />}
        </ContentPanel>
      </PageGrid>
    </PageWrapper>
  );
};

export default AfterLogin;
